import { OVERLAY_KINDS, OVERLAY_PHASES } from '../../overlay_lifecycle/overlay_lifecycle_events'
import {
	buildNextState,
	cloneState,
	createInitialState,
	emitPhaseChanged,
	getDataValue,
	getFirstDomElement,
	installOverlayLifecyclePublicApi,
	isOpenPhase,
	shouldEmit,
	startsNewTransition,
} from '../../overlay_lifecycle/overlay_lifecycle_helpers'

// Project overlays keep their existing open/close implementation. This file
// only observes the public project-overlay events and turns them into one
// queryable state object for code that needs to know where the overlay is in
// its lifecycle.
let projectTransitionId = 0
let lifecycleListenersInstalled = false
let projectOverlayState = createInitialState(OVERLAY_KINDS.PROJECT)

const getProjectMeta = function ($overlay, obj = {}) {
	const domElement = getFirstDomElement($overlay)
	const dataId = getDataValue($overlay, 'id')
	const dataType = getDataValue($overlay, 'type')
	const dataLink = getDataValue($overlay, 'link')

	return {
		id: obj.id || obj.ID || dataId || null,
		type: obj.type || dataType || 'project',
		link: obj.link || obj.url || dataLink || null,
		title: obj.title || null,
		hasElement: !!domElement,
	}
}

const setProjectPhase = function (phase, options = {}) {
	const meta = getProjectMeta(options.overlay, options.obj)
	let transitionId = projectOverlayState.transitionId

	if (options.transitionId) {
		transitionId = options.transitionId
		projectTransitionId = Math.max(projectTransitionId, transitionId)
	} else if (startsNewTransition(projectOverlayState, phase)) {
		projectTransitionId += 1
		transitionId = projectTransitionId
	}

	const previousState = projectOverlayState
	const nextState = buildNextState(projectOverlayState, phase, meta, transitionId, !!options.clearMeta)
	const emit = shouldEmit(previousState, nextState)
	projectOverlayState = nextState

	if (emit) {
		emitPhaseChanged(projectOverlayState, previousState, options.sourceEvent)
	}

	return cloneState(projectOverlayState)
}

const installPublicApi = function () {
	if (typeof window === 'undefined' || !window.laytheme) return

	window.laytheme.getProjectOverlayState = getProjectOverlayState
	window.laytheme.isProjectOverlayOpen = isProjectOverlayOpen
	window.laytheme.isProjectOverlayVisible = isProjectOverlayVisible
	window.laytheme.isProjectOverlayClosing = isProjectOverlayClosing
	installOverlayLifecyclePublicApi()
}

const installLifecycleEventListeners = function () {
	if (lifecycleListenersInstalled) return
	if (typeof window === 'undefined' || !window.laytheme || typeof window.laytheme.on !== 'function') return
	lifecycleListenersInstalled = true

	window.laytheme.on('before_content_replacement_for_project_overlay', () => {
		setProjectPhase(OVERLAY_PHASES.LOADING, { sourceEvent: 'before_content_replacement_for_project_overlay' })
	})

	window.laytheme.on('project_overlay_before_animate_in', () => {
		setProjectPhase(OVERLAY_PHASES.OPENING, { sourceEvent: 'project_overlay_before_animate_in' })
	})

	window.laytheme.on('project_overlay_open_start', ($overlay, obj) => {
		setProjectPhase(OVERLAY_PHASES.OPENING, { overlay: $overlay, obj: obj, sourceEvent: 'project_overlay_open_start' })
	})

	window.laytheme.on('project_overlay_open_end', ($overlay, obj) => {
		setProjectPhase(OVERLAY_PHASES.VISIBLE, { overlay: $overlay, obj: obj, sourceEvent: 'project_overlay_open_end' })
	})

	window.laytheme.on('project_overlay_close_start', ($overlay) => {
		setProjectPhase(OVERLAY_PHASES.CLOSING, { overlay: $overlay, sourceEvent: 'project_overlay_close_start' })
	})

	window.laytheme.on('projectoverlayclosed', () => {
		setProjectPhase(OVERLAY_PHASES.DESTROYING, { sourceEvent: 'projectoverlayclosed' })
	})

	window.laytheme.on('project_overlay_close_end', ($overlay) => {
		setProjectPhase(OVERLAY_PHASES.HIDDEN, {
			overlay: $overlay,
			clearMeta: true,
			sourceEvent: 'project_overlay_close_end',
		})
	})

	window.laytheme.on('destroyoverlay', () => {
		setProjectPhase(OVERLAY_PHASES.HIDDEN, { clearMeta: true, sourceEvent: 'destroyoverlay' })
	})
}

const getProjectOverlayState = function () {
	return cloneState(projectOverlayState)
}

const isProjectOverlayOpen = function () {
	return isOpenPhase(projectOverlayState.phase)
}

const isProjectOverlayVisible = function () {
	return projectOverlayState.phase == OVERLAY_PHASES.VISIBLE
}

const isProjectOverlayClosing = function () {
	return projectOverlayState.phase == OVERLAY_PHASES.CLOSING || projectOverlayState.phase == OVERLAY_PHASES.DESTROYING
}

const i = function () {
	installPublicApi()
	installLifecycleEventListeners()
}

const project_overlay_lifecycle_controller = {
	i: i,
	setProjectPhase: setProjectPhase,
	getProjectOverlayState: getProjectOverlayState,
	isProjectOverlayOpen: isProjectOverlayOpen,
	isProjectOverlayVisible: isProjectOverlayVisible,
	isProjectOverlayClosing: isProjectOverlayClosing,
	installLifecycleEventListeners: installLifecycleEventListeners,
}

export default project_overlay_lifecycle_controller
